import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';

interface JiraTaskTimeData {
  totalSeconds: number;
  estimateHours: number | null;
  lastSessionSeconds: number;
  sessionCount: number;
  summary: string | null;
  projectKey: string | null;
  worklogLastSyncedAt: string | null;
  updatedAt: string | null;
}

interface UseJiraTaskTimesResult {
  times: Record<string, JiraTaskTimeData>;
  loading: boolean;
  error: string | null;
  fetchTimes: (issueKeys: string[]) => Promise<void>;
  addTime: (
    issueKey: string,
    secondsToAdd: number,
    metadata?: { taskName?: string; projectKey?: string; estimateHours?: number }
  ) => Promise<void>;
  getTime: (issueKey: string) => number;
  refresh: () => Promise<void>;
}

/**
 * Persistent Jira issue time tracking.
 * Mirrors useMondayTaskTimes — caches cumulative seconds per issue key locally,
 * separate from worklogs actually pushed to Jira.
 */
export const useJiraTaskTimes = (userId?: string): UseJiraTaskTimesResult => {
  const [times, setTimes] = useState<Record<string, JiraTaskTimeData>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const fetchedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchTimes = useCallback(async (issueKeys: string[]) => {
    if (!issueKeys || issueKeys.length === 0) return;
    const normalized = issueKeys.map((k) => k.toUpperCase());
    const newKeys = normalized.filter((k) => !fetchedKeysRef.current.has(k));
    if (newKeys.length === 0) return;

    if (!isMountedRef.current) return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/jira/task-times`,
        { issueKeys: newKeys, userId },
        { withCredentials: true }
      );
      if (!isMountedRef.current) return;

      const { times: fetchedTimes } = response.data;
      newKeys.forEach((k) => fetchedKeysRef.current.add(k));
      setTimes((prev) => ({ ...prev, ...fetchedTimes }));
    } catch (err: any) {
      if (!isMountedRef.current) return;
      console.error('Failed to fetch Jira task times:', err);
      setError(err.message || 'Failed to fetch Jira task times');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [userId]);

  const addTime = useCallback(
    async (
      issueKey: string,
      secondsToAdd: number,
      metadata?: { taskName?: string; projectKey?: string; estimateHours?: number }
    ) => {
      if (secondsToAdd <= 0) return;
      const key = issueKey.toUpperCase();
      try {
        const response = await axios.post(
          `${API_URL}/jira/task-time/${encodeURIComponent(key)}/add`,
          {
            secondsToAdd,
            taskName: metadata?.taskName,
            projectKey: metadata?.projectKey,
            estimateHours: metadata?.estimateHours,
            userId
          },
          { withCredentials: true }
        );
        if (!isMountedRef.current) return;

        const { totalSeconds, sessionCount } = response.data;
        setTimes((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            totalSeconds,
            sessionCount,
            lastSessionSeconds: secondsToAdd,
            summary: metadata?.taskName || prev[key]?.summary || null,
            projectKey: metadata?.projectKey || prev[key]?.projectKey || null,
            estimateHours: metadata?.estimateHours || prev[key]?.estimateHours || null,
            worklogLastSyncedAt: prev[key]?.worklogLastSyncedAt || null,
            updatedAt: new Date().toISOString()
          }
        }));
        fetchedKeysRef.current.add(key);
      } catch (err: any) {
        console.error('Failed to add time to Jira task:', err);
        throw err;
      }
    },
    [userId]
  );

  const getTime = useCallback(
    (issueKey: string): number => times[issueKey.toUpperCase()]?.totalSeconds || 0,
    [times]
  );

  const refresh = useCallback(async () => {
    const keys = Array.from(fetchedKeysRef.current);
    if (keys.length === 0) return;
    fetchedKeysRef.current.clear();
    await fetchTimes(keys);
  }, [fetchTimes]);

  return { times, loading, error, fetchTimes, addTime, getTime, refresh };
};

export default useJiraTaskTimes;
